/**
 * Typed REST client for the AIOps Portal backend.
 * - All requests carry a JWT bearer token (from corporate SSO / Cognito).
 * - 4xx errors throw ApiError so the UI can render the server-supplied message.
 * - 5xx errors are retried once with exponential back-off (network blips).
 */

import type {
  SubmitRequestPayload,
  SubmitRequestResponse,
  PresignResponse,
  RequestStatusResponse,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Pull JWT from sessionStorage — populated by the auth callback handler */
function authHeader(): HeadersInit {
  const token = sessionStorage.getItem('aiops_jwt');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    // 5xx: retry once after 400ms
    if (res.status >= 500 && retry) {
      await new Promise(r => setTimeout(r, 400));
      return request<T>(path, init, false);
    }
    let body: any = {};
    try {
      body = await res.json();
    } catch {
      /* non-JSON body */
    }
    throw new ApiError(
      res.status,
      body.code ?? 'UNKNOWN',
      body.message ?? `HTTP ${res.status}`,
      body.details,
    );
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  /** Submit the full onboarding request */
  submitRequest(payload: SubmitRequestPayload): Promise<SubmitRequestResponse> {
    return request<SubmitRequestResponse>('/requests', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** Poll for the RITM number once the integration Lambda finishes */
  getRequestStatus(requestId: string): Promise<RequestStatusResponse> {
    return request<RequestStatusResponse>(`/requests/${encodeURIComponent(requestId)}`);
  },

  /** Get a presigned S3 POST for direct browser upload */
  presignUpload(fileName: string, contentType: string, sizeBytes: number): Promise<PresignResponse> {
    return request<PresignResponse>('/uploads/presign', {
      method: 'POST',
      body: JSON.stringify({ fileName, contentType, sizeBytes }),
    });
  },

  /**
   * Upload a file using the presigned POST. Returns the uploadId that the
   * caller must include in the corresponding use case config.
   */
  async uploadFile(
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<{ uploadId: string }> {
    const presign = await this.presignUpload(file.name, file.type || 'application/octet-stream', file.size);

    const form = new FormData();
    Object.entries(presign.fields).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);

    // Use XHR for progress events
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', presign.url, true);
      xhr.upload.onprogress = e => {
        if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new ApiError(xhr.status, 'S3_UPLOAD_FAILED', 'S3 upload failed', xhr.responseText));
      };
      xhr.onerror = () => reject(new ApiError(0, 'NETWORK', 'Network error during upload'));
      xhr.send(form);
    });

    return { uploadId: presign.uploadId };
  },
};
